import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  boundaryRowLabel,
  buildView,
  formatRankBands,
  viewToValue,
} from "./skillRankEdit";

function emptyAdvancedRow(rank) {
  return {
    rank,
    maleStart: "",
    maleEnd: "",
    femaleStart: "",
    femaleEnd: "",
  };
}

export default function SkillRankEditPanel({ seed, onChange }) {
  const [view, setView] = useState(() => buildView(seed));

  useEffect(() => {
    if (!seed) {
      return;
    }
    setView(buildView(seed));
  }, [seed]);

  const emit = (nextView) => {
    setView(nextView);
    onChange(viewToValue(nextView));
  };

  const updateDirection = (rankDirection) => {
    emit({ ...view, rank_direction: rankDirection });
  };

  const updateBoundary = (index, gender, value) => {
    const rows = view.rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [gender]: value } : row
    );
    emit({ ...view, rows });
  };

  const addBoundary = () => {
    const rows = [...view.rows];
    const blank = { male: "", female: "" };
    if (rows.length <= 1) {
      rows.push(blank);
    } else {
      rows.splice(rows.length - 1, 0, blank);
    }
    emit({ ...view, rows });
  };

  const removeBoundary = (index) => {
    if (index === 0) {
      return;
    }
    emit({ ...view, rows: view.rows.filter((_, rowIndex) => rowIndex !== index) });
  };

  const updateAdvanced = (index, field, value) => {
    const advancedRows = view.advancedRows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [field]: value } : row
    );
    emit({ ...view, advancedRows });
  };

  const addAdvancedRank = () => {
    const nextRank = view.advancedRows.reduce(
      (max, row) => Math.max(max, Number(row.rank) || 0),
      0
    ) + 1;
    emit({
      ...view,
      advancedRows: [...view.advancedRows, emptyAdvancedRow(nextRank)],
    });
  };

  const removeAdvancedRank = (index) => {
    emit({
      ...view,
      advancedRows: view.advancedRows.filter((_, rowIndex) => rowIndex !== index),
    });
  };

  const saved = viewToValue(view);

  return (
    <Stack spacing={2}>
      <Typography variant="h6">評分設定</Typography>
      <FormControl size="small" sx={{ maxWidth: 280 }}>
        <InputLabel id="rank-direction-label">排行方向</InputLabel>
        <Select
          labelId="rank-direction-label"
          label="排行方向"
          value={view.rank_direction}
          onChange={(event) => updateDirection(event.target.value)}
        >
          <MenuItem value="desc">desc 越大越高</MenuItem>
          <MenuItem value="asc">asc 越小越高</MenuItem>
        </Select>
      </FormControl>
      <Typography variant="body2" color="text.secondary">
        {view.mode === "boundary"
          ? "Base 是第 1 級的下限。每一列是該級的上限，同時是下一級的起點。最後一個數字以上為最高級，不會儲存 Base。"
          : "此評分不是連續區間，請分別輸入開始與結束。空白的結束值代表沒有上限。"}
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Rank</TableCell>
            <TableCell>Male Range</TableCell>
            <TableCell>Female Range</TableCell>
            <TableCell align="right"> </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {view.mode === "boundary"
            ? view.rows.map((row, index) => (
              <TableRow key={`boundary-${index}`}>
                <TableCell>{boundaryRowLabel(index)}</TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={row.male}
                    onChange={(event) => updateBoundary(index, "male", event.target.value)}
                    inputProps={{ inputMode: "decimal" }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={row.female}
                    onChange={(event) => updateBoundary(index, "female", event.target.value)}
                    inputProps={{ inputMode: "decimal" }}
                  />
                </TableCell>
                <TableCell align="right">
                  {index > 0 ? (
                    <IconButton aria-label="移除區間" onClick={() => removeBoundary(index)}>
                      <DeleteIcon />
                    </IconButton>
                  ) : null}
                </TableCell>
              </TableRow>
            ))
            : view.advancedRows.map((row, index) => (
              <TableRow key={`advanced-${row.rank}-${index}`}>
                <TableCell>{row.rank}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      label="開始"
                      value={row.maleStart}
                      onChange={(event) => updateAdvanced(index, "maleStart", event.target.value)}
                    />
                    <TextField
                      size="small"
                      label="結束"
                      value={row.maleEnd}
                      onChange={(event) => updateAdvanced(index, "maleEnd", event.target.value)}
                    />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      label="開始"
                      value={row.femaleStart}
                      onChange={(event) => updateAdvanced(index, "femaleStart", event.target.value)}
                    />
                    <TextField
                      size="small"
                      label="結束"
                      value={row.femaleEnd}
                      onChange={(event) => updateAdvanced(index, "femaleEnd", event.target.value)}
                    />
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <IconButton aria-label="移除等級" onClick={() => removeAdvancedRank(index)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={view.mode === "boundary" ? addBoundary : addAdvancedRank}
        >
          {view.mode === "boundary" ? "新增區間" : "新增等級"}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary">
        男：{formatRankBands(saved.skill_rank_male)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        女：{formatRankBands(saved.skill_rank_female)}
      </Typography>
    </Stack>
  );
}
